import type { LessonListing } from "@/features/workspaces/types/lessonListings.ts";
import type {
  WorkspaceInformation,
  WorkspaceMember,
} from "@/features/workspaces/types/workspaceInformation.ts";
import type { WorkspacesResponse } from "@/features/workspaces/types/workspaceWidget.ts";
import type { WorkspaceInformationResponse } from "../types/workspaceInformation";

import { create } from "zustand";

import { PaginationMeta } from "@/features/workspaces/types/invitations.ts";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";

interface WorkspaceInfo {
  data: WorkspaceInformation | undefined;
  setData: (data: WorkspaceInformation | undefined) => void;
  members: WorkspaceMember[] | undefined;
  setMembers: (members: WorkspaceMember[]) => void;
  lessonListings: LessonListing[] | undefined;
  setLessonListings: (lessonListing: LessonListing[]) => void;
  lessonPagination: PaginationMeta | undefined;
  setLessonPagination: (pagination: PaginationMeta) => void;
  widgetData: WorkspacesResponse | undefined;
  setWidgetData: (widgetData: WorkspacesResponse | undefined) => void;
  updateWorkspaceName: (name: string) => void;
  updateWorkspaceLogo: (logoUrl: string) => void;
  refetch: (workspaceId: string) => Promise<void>;
}

const useWorkspaceInformationStore = create<WorkspaceInfo>()((set, get) => ({
  data: undefined,
  setData: (data) => {
    set(() => ({ data: data }));
  },
  members: undefined,
  setMembers: (members: WorkspaceMember[]) => {
    set(() => ({ members: members }));
  },
  lessonListings: undefined,
  setLessonListings: (lessonListings: LessonListing[]) => {
    set(() => ({ lessonListings: lessonListings }));
  },
  lessonPagination: undefined,
  setLessonPagination: (pagination: PaginationMeta) => {
    set(() => ({ lessonPagination: pagination }));
  },
  widgetData: undefined,
  setWidgetData: (widgetData: WorkspacesResponse | undefined) => {
    set(() => ({ widgetData: widgetData }));
  },
  updateWorkspaceName: (name: string) => {
    const { data } = get();

    if (data) {
      set({
        data: {
          ...data,
          name: name,
        },
      });
    }
  },
  updateWorkspaceLogo: (logoUrl: string) => {
    const { data } = get();

    if (data) {
      set({
        data: {
          ...data,
          logo: logoUrl,
        },
      });
    }
  },
  refetch: async (workspaceId: string) => {
    try {
      const {
        data: { data },
      } = await axiosApiInstance<WorkspaceInformationResponse>(
        `${API_ENDPOINTS.WORKSPACES.WORKSPACES}/${workspaceId}/basics`,
      );

      set({ data: data, members: data.members });
    } catch (error) {
      console.error("Failed to refetch workspace information:", error);
    }
  },
}));

export default useWorkspaceInformationStore;
